import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Loader2, Building2, Copy, Share2, Landmark, AlertTriangle, Download,
  ArrowUpDown, Search, ChevronDown, ChevronUp, CreditCard, Wallet, TrendingUp,
  DollarSign, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

function SummaryCard({ label, value, sub, icon: Icon, color }) {
  return (
    <Card className="border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}-500`} />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}{sub ? ` \u00b7 ${sub}` : ''}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyDrillDown({ companyId, companyRow, onClose, onCreateAccount }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    superAdminAPI.getCompanyFundingHistory(companyId)
      .then(setHistory)
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [companyId]);

  const copyNumber = () => {
    navigator.clipboard.writeText(companyRow.virtual_account_number);
    toast.success('Account number copied');
  };

  const shareDetails = () => {
    const msg = `Your Pebbles funding account details are ready.\n\nAccount Name: ${companyRow.account_name}\nAccount Number: ${companyRow.virtual_account_number}\nBank: Assets MFB\nBank Code: 090160\n\nPlease send funds to this account to load your Pebbles wallet. Your dashboard balance will update automatically once funds are received.`;
    navigator.clipboard.writeText(msg);
    toast.success('Account details copied — ready to share');
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await superAdminAPI.createVirtualAccount(companyId);
      toast.success('Virtual account created');
      setShowConfirm(false);
      onCreateAccount?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally { setCreating(false); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-pink-500" />
          {companyRow.company_name}
        </DialogTitle>
      </DialogHeader>

      {/* Account Details */}
      {companyRow.has_virtual_account ? (
        <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-200 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Funding Account</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyNumber} data-testid="drilldown-copy-btn"
                className="h-7 text-xs border-emerald-300 text-emerald-700"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
              <Button variant="outline" size="sm" onClick={shareDetails} data-testid="drilldown-share-btn"
                className="h-7 text-xs border-emerald-300 text-emerald-700"><Share2 className="w-3 h-3 mr-1" /> Share</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-[10px] text-gray-500 uppercase">Account Number</p><p className="text-lg font-bold text-emerald-700 font-mono">{companyRow.virtual_account_number}</p></div>
            <div><p className="text-[10px] text-gray-500 uppercase">Account Name</p><p className="font-semibold text-gray-900">{companyRow.account_name}</p></div>
            <div><p className="text-[10px] text-gray-500 uppercase">Bank</p><p className="font-medium text-gray-900">{companyRow.bank_name}</p></div>
            <div><p className="text-[10px] text-gray-500 uppercase">Bank Code</p><p className="font-medium text-gray-900">090160</p></div>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-orange-50/50 rounded-xl border border-orange-200 text-center space-y-3">
          <Landmark className="w-8 h-8 text-orange-400 mx-auto" />
          <p className="text-sm text-gray-700">No virtual account created yet</p>
          <Button onClick={() => setShowConfirm(true)} className="bg-orange-500 hover:bg-orange-600 text-white text-xs" data-testid="drilldown-create-btn">
            <Landmark className="w-3.5 h-3.5 mr-1.5" /> Create Virtual Account
          </Button>
          <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle className="text-base">Confirm Account Creation</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">This will create a real production account with Assets MFB under master account 1000120542. Confirm?</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={creating} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" data-testid="drilldown-confirm-create-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Wallet Breakdown */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
      ) : history?.wallet ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            {[
              { label: 'Available', value: history.wallet.available_balance, color: 'emerald' },
              { label: 'Balance', value: history.wallet.balance, color: 'blue' },
              { label: 'Total Funded', value: history.wallet.total_funded, color: 'green' },
              { label: 'Total Disbursed', value: history.wallet.total_disbursed, color: 'pink' },
            ].map(s => (
              <div key={s.label} className={`p-3 rounded-lg bg-${s.color}-50/50 border border-${s.color}-100`}>
                <p className="text-[10px] text-gray-500 uppercase">{s.label}</p>
                <p className="text-sm font-bold text-gray-900">NGN {Number(s.value || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>

          {/* Inflows */}
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Funding History ({history.inflows?.length || 0})</h4>
            {history.inflows?.length > 0 ? (
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {history.inflows.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.sender_name}</p>
                      <p className="text-gray-500 truncate">{t.description}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-bold text-emerald-600">+NGN {Number(t.amount).toLocaleString()}</p>
                      <p className="text-gray-400">{new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-gray-400 text-center py-3">No inflows yet</p>}
          </div>

          {/* Disbursements */}
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Disbursements ({history.disbursements?.length || 0})</h4>
            {history.disbursements?.length > 0 ? (
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {history.disbursements.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.description || t.category || t.type}</p>
                      <p className="text-gray-500 truncate">{t.reference}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="font-bold text-pink-600">-NGN {Number(t.amount).toLocaleString()}</p>
                      <p className="text-gray-400">{new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-gray-400 text-center py-3">No disbursements yet</p>}
          </div>
        </>
      ) : (
        <p className="text-xs text-gray-400 text-center py-6">No wallet provisioned for this company</p>
      )}
    </DialogContent>
  );
}

export default function VirtualAccountsOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('company_name');
  const [sortDir, setSortDir] = useState('asc');
  const [drillDown, setDrillDown] = useState(null);

  const load = async () => {
    try {
      setData(await superAdminAPI.getVirtualAccountsOverview());
    } catch { toast.error('Failed to load virtual accounts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    if (!data?.companies) return [];
    let rows = data.companies;
    if (search) rows = rows.filter(r => r.company_name.toLowerCase().includes(search.toLowerCase()));
    if (filter === 'active') rows = rows.filter(r => r.has_virtual_account);
    if (filter === 'not_created') rows = rows.filter(r => !r.has_virtual_account);
    rows = [...rows].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, search, filter, sortBy, sortDir]);

  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = ['Company Name', 'Account Number', 'Account Name', 'Bank', 'Wallet Balance', 'Total Funded', 'Total Disbursed', 'Status', 'Created At'];
    const rows = filtered.map(r => [
      r.company_name, r.virtual_account_number || '', r.account_name, r.bank_name,
      r.wallet_balance, r.total_funded, r.total_disbursed,
      r.has_virtual_account ? 'Active' : 'Not Created', r.created_at,
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `virtual-accounts-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-pink-500" /> : <ChevronDown className="w-3 h-3 text-pink-500" />;
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="virtual-accounts-overview">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Virtual Accounts</h1>
          <p className="text-sm text-gray-500">Central command for all client wallets and accounts</p>
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn" className="h-9 text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Summary Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total Companies" value={summary.total_companies} icon={Building2} color="blue" />
        <SummaryCard label="Active Accounts" value={summary.active_accounts} icon={CreditCard} color="emerald" />
        <SummaryCard label="Needs Action" value={summary.needs_action} icon={AlertTriangle} color="orange" />
        <SummaryCard label="Funded Today" value={`NGN ${Number(summary.total_funded_today || 0).toLocaleString()}`} icon={TrendingUp} color="green" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..."
            className="h-9 pl-9 text-sm" data-testid="search-companies" />
        </div>
        <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'not_created', label: 'Not Created' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${filter === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left py-3 px-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('company_name')}>
                <span className="flex items-center gap-1">Company <SortIcon col="company_name" /></span>
              </th>
              <th className="text-left py-3 px-3 font-semibold text-gray-600">Account Number</th>
              <th className="text-left py-3 px-3 font-semibold text-gray-600 hidden lg:table-cell">Account Name</th>
              <th className="text-right py-3 px-3 font-semibold text-gray-600 cursor-pointer select-none" onClick={() => toggleSort('wallet_balance')}>
                <span className="flex items-center gap-1 justify-end">Balance <SortIcon col="wallet_balance" /></span>
              </th>
              <th className="text-right py-3 px-3 font-semibold text-gray-600 hidden md:table-cell cursor-pointer select-none" onClick={() => toggleSort('total_funded')}>
                <span className="flex items-center gap-1 justify-end">Total Funded <SortIcon col="total_funded" /></span>
              </th>
              <th className="text-right py-3 px-3 font-semibold text-gray-600 hidden md:table-cell">Disbursed</th>
              <th className="text-center py-3 px-3 font-semibold text-gray-600">Status</th>
              <th className="text-center py-3 px-3 font-semibold text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(r => (
              <tr key={r.company_id} className="hover:bg-gray-50/50 cursor-pointer" data-testid={`va-row-${r.company_id}`}
                onClick={() => setDrillDown(r)}>
                <td className="py-3 px-3">
                  <p className="font-medium text-gray-900">{r.company_name}</p>
                  <p className="text-gray-400 truncate max-w-[200px]">{r.company_email}</p>
                </td>
                <td className="py-3 px-3">
                  {r.virtual_account_number ? (
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-bold text-emerald-700" data-testid={`va-number-${r.company_id}`}>{r.virtual_account_number}</span>
                      <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(r.virtual_account_number); toast.success('Copied'); }}
                        className="p-0.5 rounded hover:bg-emerald-50 text-emerald-600">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ) : <span className="text-gray-400">&mdash;</span>}
                </td>
                <td className="py-3 px-3 text-gray-700 hidden lg:table-cell">{r.account_name}</td>
                <td className="py-3 px-3 text-right font-medium text-gray-900">NGN {Number(r.wallet_balance).toLocaleString()}</td>
                <td className="py-3 px-3 text-right text-gray-600 hidden md:table-cell">NGN {Number(r.total_funded).toLocaleString()}</td>
                <td className="py-3 px-3 text-right text-gray-600 hidden md:table-cell">NGN {Number(r.total_disbursed).toLocaleString()}</td>
                <td className="py-3 px-3 text-center">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${r.has_virtual_account ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-500'}`}
                    data-testid={`va-status-${r.company_id}`}>
                    {r.has_virtual_account ? 'Active' : 'Not Created'}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-gray-500 hover:text-pink-600"
                    onClick={e => { e.stopPropagation(); setDrillDown(r); }} data-testid={`va-action-${r.company_id}`}>
                    {r.has_virtual_account ? 'Details' : 'Create'}
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-gray-400">No companies match your filters</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drill Down Dialog */}
      <Dialog open={!!drillDown} onOpenChange={() => setDrillDown(null)}>
        {drillDown && (
          <CompanyDrillDown
            companyId={drillDown.company_id}
            companyRow={drillDown}
            onClose={() => setDrillDown(null)}
            onCreateAccount={() => { setDrillDown(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}
